import { getLogger } from '@logtape/logtape';
import { ComboboxItem } from '@mantine/core';
import { FilledContentRelationshipField } from '@prismicio/client';

import { LocationHub } from '@/models/EnrichedPrismicDocuments.ts';
import { PractitionerDataCurDto, PractitionerDocCirrusDto } from '@/models/PractitionerDto.ts';
import { Appointment } from '@/models/appointment/Appointment.ts';
import {
    EnrichedAppointmentType,
    EnrichedPractitionerData,
    EnrichedPractitionerDataCurData,
    EnrichedPractitionerDocCirrusData,
    Practice,
    Practitioner,
    PractitionerCalendar,
    UnMappedScheduleType,
} from '@/models/appointment/EnrichedPractitionerData.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { DataCurCalendar } from '@/models/data-cur/DataCurCalendar.ts';
import { DataCurPracUserMapping } from '@/models/data-cur/DataCurPracUserMapping.ts';
import { DataCurTimeScheme } from '@/models/data-cur/DataCurTimeScheme.ts';
import { DCCalendarConsultation, Timings } from '@/models/doc-cirrus/DCCalendar.ts';
import { DCScheduleType } from '@/models/doc-cirrus/DCScheduleType.ts';
import { DCScheduleTypeMapping } from '@/models/doc-cirrus/DCScheduleTypeMapping.ts';
import { DCUserMapping } from '@/models/doc-cirrus/DCUserMapping.ts';
import { HubDocument, ProfessionalTypeDocument, ServiceOrTreatmentDocument } from '@/models/prismicTypes.ts';
import { NotificationCategory, NotificationType } from '@/models/pvs/NotificationSettings.ts';

import { getTextFromRichTextField } from '@/utils/prismicUtils.ts';

const logger = getLogger(['util', 'cached-data']);

export const joinAppointmentServicesKeys = (appointment: Appointment): string =>
    appointment.data.services.map((service) => service.key).join(',');

/**
 * Simple conversion of DataCur AZ time schemes to DocCirrus timing format
 * Only processes AZ (Arbeitszeit) type - when doctor is available
 */
const convertDataCurAZTimingSchemesToTimings = (time_schemes: DataCurTimeScheme[], resource_id: string): Timings => {
    // Only get AZ (Arbeitszeit) schemes for this resource
    const az_schemes = time_schemes.filter((scheme) => scheme.resource_id === resource_id && scheme.type === 'AZ');

    // Convert DataCur day codes to numeric format (1=Monday, 7=Sunday)
    const week_day_map: Record<string, number> = {
        MO: 1,
        TU: 2,
        WE: 3,
        TH: 4,
        FR: 5,
        SA: 6,
        SU: 7,
    };

    const base_consultations: DCCalendarConsultation[] = az_schemes.map((scheme) => {
        // Parse week days
        const days_of_week = scheme.week_days
            ? scheme.week_days
                  .split(',')
                  .map((day) => week_day_map[day.trim()])
                  .filter((day) => day !== undefined)
            : [];

        // Parse time strings (HH:MM format)
        const [start_hour, start_minute] = scheme.start_time.split(':').map(Number);
        const [end_hour, end_minute] = scheme.end_time.split(':').map(Number);

        return {
            id: `datacur-az-${scheme.id}`,
            schedule_type_ids: [],
            repetition_settings: [],
            color: scheme.color ?? '#f4f9f5', // Use AZ scheme color
            day_range: [],
            days_of_week: days_of_week,
            start_time: [start_hour, start_minute],
            end_time: [end_hour, end_minute],
            public_insurance: true,
            private_insurance: true,
        };
    });

    return {
        base: base_consultations,
        temporary: [], // No temporary for simple AZ implementation
    };
};

const getPractitionerNameFromUser = (user: PractitionerDocCirrusDto): string =>
    getTextFromRichTextField(user.prismic_profile.data.display_name) ||
    user.dc_user.name.first_name + user.dc_user.name.last_name;

// TODO: PVS-MIGRATION use strategy instead
const getPractitionerNameFromUserDataCur = (user: PractitionerDataCurDto): string =>
    getTextFromRichTextField(user.prismic_profile.data.display_name) || 'Missing Name'; // TODO: PVS-MIGRATION user strategy

const getPractitionerIdFromUser = (user: PractitionerDocCirrusDto): string => user.dc_user_mapping.user_id;

export const enrichPractitionerCachedData = (
    linked_users: PractitionerDocCirrusDto[] | PractitionerDataCurDto[],
    customer_id: string,
    professional_types_map: Map<string, ProfessionalTypeDocument>,
    service_or_treatment_types_map?: Map<string, ServiceOrTreatmentDocument>
): EnrichedPractitionerData => {
    let enriched_data: EnrichedPractitionerData;

    // Assumption: all users belong to the same instance_id
    let instance_id: string = '';

    if (linked_users.length === 0) {
        return {
            pvs_provider: PVSProvider.DATA_CUR,
            instance_id: '',
            practitioners: [],
            appointment_types: [],
            unmapped_schedule_types: [],
            practices: undefined,
        };
    }

    switch (linked_users[0].pvs_provider) {
        case PVSProvider.DOC_CIRRUS: {
            instance_id = linked_users[0].dc_user_mapping.dc_instance_id;
            enriched_data = enrichPractitionerDocCirrusCachedData(
                linked_users as PractitionerDocCirrusDto[],
                customer_id,
                instance_id,
                professional_types_map
            );
            break;
        }
        case PVSProvider.DATA_CUR: {
            instance_id = linked_users[0].data_cur_user_mapping.dc_instance_id;
            enriched_data = enrichPractitionerDataCurCachedData(
                linked_users as PractitionerDataCurDto[],
                customer_id,
                instance_id,
                professional_types_map,
                service_or_treatment_types_map
            );
            break;
        }
        default: {
            logger.error('Unsupported PVS provider on cached data enrichment');
            enriched_data = {
                pvs_provider: PVSProvider.DATA_CUR,
                instance_id: '',
                practitioners: [],
                appointment_types: [],
                unmapped_schedule_types: [],
                practices: undefined,
            };
            break;
        }
    }

    // Add instance_id to the enriched data
    enriched_data.instance_id = instance_id;

    return enriched_data;
};

export const enrichPractitionerDataCurCachedData = (
    linked_data_cur_users: PractitionerDataCurDto[],
    customer_id: string,
    instance_id: string,
    professional_types_map: Map<string, ProfessionalTypeDocument>,
    service_or_treatment_types_map?: Map<string, ServiceOrTreatmentDocument>
): EnrichedPractitionerDataCurData => {
    const dummy_practice: Practice = {
        customer_id: customer_id,
        instance_id: instance_id,
        prismic_hub: normalizePrismicHubData(linked_data_cur_users[0].prismic_hub),
    };

    const map_appointment_types: Map<string, EnrichedAppointmentType> = new Map();

    // Data cur has no support for them now.
    // const map_unmapped_schedule_types: Map<string, UnMappedScheduleType> = new Map();

    const practitioners = linked_data_cur_users.map((user) => {
        // Get full professional type document from prismic
        const professional_type_id = (
            user.prismic_profile.data.type as FilledContentRelationshipField<'professional_type'>
        )?.id;
        const professional_type = professional_types_map.get(professional_type_id);

        // Build enriched practitioner
        const calendar_ids: Set<string> = new Set(
            user.data_cur_user_mapping.dc_resources_data
                .filter((r) => r.dc_instance_id === user.data_cur_user_mapping.dc_instance_id)
                .map((resource) => resource.dc_resource_id)
        );
        const calendars: DataCurCalendar[] =
            user.data_cur_resources.filter((resource) => calendar_ids.has(resource.id)) ?? [];

        // Get all appointment types for the practitioner, they should be the same for all practitioners in the same instance though
        const appointment_type_ids = user.data_cur_event_type_mappings
            .filter((apt) => apt.dc_instance_id === instance_id)
            .map((apt_type) => apt_type.appointment_type_id);

        const practitioner: Practitioner = {
            pvs_provider: user.pvs_provider,
            id: user.data_cur_user_mapping.user_id,
            name: getPractitionerNameFromUserDataCur(user),
            prismic_key: user.prismic_profile.id,
            calendars: calendars.map((calendar) => {
                return {
                    practitioner_id: user.data_cur_user_mapping.user_id,
                    pvs: calendar,
                    appointment_type_ids: appointment_type_ids,
                    unmapped_schedule_type_ids: [], // not supported yet on data cur
                };
            }),
            pcs_stack_id: user.data_cur_user_mapping.pcs_stack_id,
            pvs: {
                practitioner_id: user.data_cur_user_mapping.user_id,
                instance_id: user.data_cur_user_mapping.dc_instance_id,
            },
            prismic_profile: user.prismic_profile,
            prismic_professional_type: professional_type,
            practices: [dummy_practice], // fake practice for now
            is_notification_enabled: getApptConfirmationNotificationEnabledFromUser(user.data_cur_user_mapping),
            ooo_time_schemes: user.data_cur_ooo_time_schemes || [],
            time_schemes: user.data_cur_time_schemes || [],
            calendar_timings_map: new Map<string, Timings>(),
        };

        // Add ALL OOO and time schemes from this user to the practitioner

        // Create calendar timings map for DataCur - only AZ availability
        for (const calendar of calendars) {
            const timings = convertDataCurAZTimingSchemesToTimings(user.data_cur_time_schemes || [], calendar.id);
            practitioner.calendar_timings_map?.set(calendar.id, timings);
        }

        for (const event_type_map of user.data_cur_event_type_mappings) {
            // Assumption all appointment are mapped
            const existing_appointment_type = map_appointment_types.get(event_type_map.appointment_type_id);

            if (existing_appointment_type) {
                const updated_appointment_type = {
                    ...existing_appointment_type,
                };

                // If it exists already just add practitioner_id and calendar_ids
                updated_appointment_type.offered_by_practitioner_ids.push(practitioner.id);
                updated_appointment_type.pvs.pvs_calendar_ids.push(
                    ...practitioner.calendars.map((calendar) => calendar.pvs.id)
                );

                // Save the updated element
                map_appointment_types.set(event_type_map.appointment_type_id, updated_appointment_type);
            } else {
                map_appointment_types.set(event_type_map.appointment_type_id, {
                    ...event_type_map,
                    offered_by_practitioner_ids: [practitioner.id],
                    pvs: {
                        pvs_calendar_ids: practitioner.calendars.map((calendar) => calendar.pvs.id),
                        pvs_schedule_type_id: event_type_map.dc_event_type_id,
                        pvs_instance_id: event_type_map.dc_instance_id,
                        pvs_event_type_code: event_type_map.event_code,
                    },
                    id: event_type_map.appointment_type_id,
                    mapped: true,
                    name:
                        getTextFromRichTextField(
                            service_or_treatment_types_map?.get(event_type_map.prismic_key)?.data.name
                        ) ?? '',
                });
            }
        }

        return practitioner;
    });

    const aggregated_result: EnrichedPractitionerDataCurData = {
        pvs_provider: PVSProvider.DATA_CUR,
        instance_id: instance_id,
        practitioners: practitioners,
        appointment_types: [...map_appointment_types.values()],
        unmapped_schedule_types: [],
        practices: undefined,
    };

    return aggregated_result;
};

// TODO: add unit test on the entire file :D
export const enrichPractitionerDocCirrusCachedData = (
    linked_doc_cirrus_users: PractitionerDocCirrusDto[],
    customer_id: string,
    instance_id: string,
    professional_types_map: Map<string, ProfessionalTypeDocument>
): EnrichedPractitionerDocCirrusData => {
    const practices: Practice[] = extractUniquePracticesFromLinkedUsers(linked_doc_cirrus_users, customer_id);

    const map_appointment_types: Map<string, EnrichedAppointmentType> = new Map();
    const map_unmapped_schedule_types: Map<string, UnMappedScheduleType> = new Map();

    const practitioners = linked_doc_cirrus_users.map((user) => {
        // Get full professional type document from prismic
        const professional_type_id = (
            user.prismic_profile.data.type as FilledContentRelationshipField<'professional_type'>
        )?.id;
        const professional_type = professional_types_map.get(professional_type_id);

        // Build enriched practitioner
        const practitioner: Practitioner = {
            pvs_provider: user.pvs_provider,
            id: getPractitionerIdFromUser(user),
            name: getPractitionerNameFromUser(user),
            prismic_key: user.prismic_profile.id,
            calendars: extractPractitionerCalendarsFromDocCirrusUser(user),
            pcs_stack_id: user.dc_user_mapping.pcs_stack_id,
            pvs: {
                practitioner_id: user.dc_user.user_id,
                instance_id: user.dc_user.instance_id,
            },
            prismic_profile: user.prismic_profile,
            prismic_professional_type: professional_type,
            practices: extractPracticesFromLinkedUser(user, customer_id),
            is_notification_enabled: getApptConfirmationNotificationEnabledFromUser(user.dc_user_mapping),
            ooo_schedules: user.dc_ooo_schedules || [],
        };

        for (const schedule_type of user.dc_schedule_types) {
            if (schedule_type.is_mapped) {
                // If mapped enrich appointment_types
                // This can be done more efficient building a map to find apt_id from schedule_id easier.
                const appointment_type = user.dc_schedule_type_mappings.find(
                    (map) => map.dc_schedule_type_id === schedule_type.id
                );

                if (appointment_type) {
                    const existing_appointment_type = map_appointment_types.get(appointment_type.appointment_type_id);
                    if (existing_appointment_type) {
                        const updated_appointment_type = {
                            ...existing_appointment_type,
                        };

                        // If it exists already just add practitioner_id and calendar_ids
                        updated_appointment_type.offered_by_practitioner_ids.push(practitioner.id);
                        for (const id of schedule_type.calendar_ids) {
                            updated_appointment_type.pvs.pvs_calendar_ids.push(id);
                        }

                        // Save the updated element
                        map_appointment_types.set(appointment_type.appointment_type_id, updated_appointment_type);
                    } else {
                        map_appointment_types.set(
                            appointment_type.appointment_type_id,
                            mapDCInfoToAppointmentType(schedule_type, appointment_type, practitioner.id)
                        );
                    }
                } else {
                    // error TBD
                    logger.warn('Missing appointment type for mapped schedule_type_id {scheduleTypeId}', {
                        scheduleTypeId: schedule_type.id,
                    });
                }
            } else {
                // If unmapped enrich unmapped_schedule_types
                const existing_unmapped_schedule_type = map_unmapped_schedule_types.get(schedule_type.id);
                if (existing_unmapped_schedule_type) {
                    const update_unmapped_schedule_type = {
                        ...existing_unmapped_schedule_type,
                    };
                    // If it exist already just add practitioner_id and calendar_ids
                    update_unmapped_schedule_type.offered_by_practitioner_ids.push(practitioner.id);
                    for (const id of schedule_type.calendar_ids) {
                        update_unmapped_schedule_type.pvs.pvs_calendar_ids.push(id);
                    }

                    // Save the updated element
                    map_unmapped_schedule_types.set(schedule_type.id, update_unmapped_schedule_type);
                } else {
                    // If new just add it
                    const enriched_schedule_type: UnMappedScheduleType = {
                        name: schedule_type.name,
                        mapped: false,
                        offered_by_practitioner_ids: [practitioner.id],
                        pvs: {
                            pvs_instance_id: schedule_type.instance_id,
                            pvs_schedule_type_id: schedule_type.id,
                            pvs_calendar_ids: schedule_type.calendar_ids,
                        },
                        duration: schedule_type.duration,
                    };

                    map_unmapped_schedule_types.set(schedule_type.id, enriched_schedule_type);
                }
            }
        }

        return practitioner;
    });

    const aggregated_result: EnrichedPractitionerDocCirrusData = {
        pvs_provider: PVSProvider.DOC_CIRRUS,
        instance_id: instance_id,
        practices: practices,
        practitioners: practitioners,
        appointment_types: [...map_appointment_types.values()],
        unmapped_schedule_types: [...map_unmapped_schedule_types.values()],
    };

    return aggregated_result;
};

// Filter appointment or schedule appointment by available practitioner
const filterByAvailablePractitioner = <T extends { offered_by_practitioner_ids: string[] }>(
    data: T[],
    available_practitioner: Practitioner[]
): T[] => {
    const filtered_data: T[] = [];
    for (const apt of data) {
        const is_offered_by_practice = apt.offered_by_practitioner_ids.some((id) =>
            available_practitioner.find((practitioner) => practitioner.id === id)
        );

        if (is_offered_by_practice) {
            filtered_data.push(apt);
        }
    }

    return filtered_data;
};

export const filterEnrichedDataByPracticeId = (
    data: EnrichedPractitionerData,
    practice_pvs_id?: string
): EnrichedPractitionerData | undefined => {
    // Don't filter if a key is not provided or if it's a data cur since it doesn't have a practice
    if (data.pvs_provider === PVSProvider.DATA_CUR || !practice_pvs_id) {
        return data;
    }

    // Filter practitioner and offered services by practice
    const practitioner_filtered_by_practice = data.practitioners.filter((practitioner) =>
        practitioner.practices.some((loc) => loc.pvs?.id === practice_pvs_id)
    );

    // Filter by practice
    const appointment_types_filtered_by_practice = filterByAvailablePractitioner(
        data.appointment_types,
        practitioner_filtered_by_practice
    );

    // Filter by practice
    const unmapped_schedule_types_filtered_by_practice = filterByAvailablePractitioner(
        data.unmapped_schedule_types,
        practitioner_filtered_by_practice
    );

    const filtered_data: EnrichedPractitionerData = {
        pvs_provider: data.pvs_provider,
        instance_id: data.instance_id,
        practices: data.practices, // return all practice anyway
        practitioners: practitioner_filtered_by_practice,
        appointment_types: appointment_types_filtered_by_practice,
        unmapped_schedule_types: unmapped_schedule_types_filtered_by_practice,
    };

    return filtered_data;
};

const mapDCInfoToAppointmentType = (
    dc_schedule_type: DCScheduleType, // TODO: PVS-MIGRATION rename to DataCurResource
    dc_schedule_type_mapping: DCScheduleTypeMapping, // TODO: PVS-MIGRATION rename to DataCurResource
    practitioner_id: string
): EnrichedAppointmentType => {
    const { dc_schedule_type_id, dc_instance_id, appointment_type_id, ...additional_info } = dc_schedule_type_mapping;

    const apt_type: EnrichedAppointmentType = {
        id: appointment_type_id,
        mapped: true,
        offered_by_practitioner_ids: [practitioner_id],
        name: dc_schedule_type.name,
        ...additional_info,
        duration: dc_schedule_type.duration,
        pvs: {
            pvs_calendar_ids: dc_schedule_type.calendar_ids,
            pvs_schedule_type_id: dc_schedule_type_id,
            pvs_instance_id: dc_instance_id,
        },
    };

    return apt_type;
};

const extractPractitionerCalendarsFromDocCirrusUser = (user: PractitionerDocCirrusDto): PractitionerCalendar[] => {
    let calendars: PractitionerCalendar[] = [];

    const schedule_type_map: Map<string, DCScheduleType> = new Map(
        user.dc_schedule_types.map((type) => [type.id, type])
    );

    if (user.pvs_provider === PVSProvider.DOC_CIRRUS) {
        calendars = user.dc_calendars.map((calendar) => {
            const appointment_type_ids: string[] = user.dc_schedule_type_mappings
                .filter((map) => schedule_type_map.get(map.dc_schedule_type_id)?.calendar_ids.includes(calendar.id))
                .map((map) => map.appointment_type_id);
            const unmapped_schedule_type_ids: string[] = user.dc_schedule_types
                .filter((type) => !type.is_mapped && type.calendar_ids.includes(calendar.id))
                .map((type) => type.id);
            const enriched_calendar: PractitionerCalendar = {
                practitioner_id: getPractitionerIdFromUser(user),
                pvs: calendar,
                appointment_type_ids: appointment_type_ids,
                unmapped_schedule_type_ids: unmapped_schedule_type_ids,
            };

            return enriched_calendar;
        });
    }

    return calendars;
};

const extractPracticesFromLinkedUser = (user: PractitionerDocCirrusDto, customer_id: string): Practice[] => {
    const instance_id = user.dc_user_mapping.dc_instance_id;
    const locations = user.dc_user.account.locations.map((loc) => {
        // Enrich each practice
        const practice: Practice = {
            customer_id: customer_id,
            instance_id: instance_id,
            prismic_hub: normalizePrismicHubData(user.prismic_hub),
            pvs: loc,
        };

        return practice;
    });

    return locations;
};

/**
 *  Extract unique practices from users and enrich with customer id and prismic hub id
 *  Users should belong to the same customer_id, there is no check here.
 */
export const extractUniquePracticesFromLinkedUsers = (
    users: PractitionerDocCirrusDto[],
    customer_id: string
): Practice[] => {
    const enriched_practices = users.flatMap((user) => {
        return extractPracticesFromLinkedUser(user, customer_id);
    });

    const enriched_practice_map = new Map<string, Practice>();
    for (const item of enriched_practices) {
        if (!item.pvs) {
            logger.error('No pvs property found for practice object, required for DocCirrus object', {
                practice: item,
            });
            continue;
        }
        enriched_practice_map.set(item.pvs.id, item);
    }

    return [...enriched_practice_map.values()];
};

export const createPracticeOptionsForInstance = (
    enriched_practitioner_data: EnrichedPractitionerData[] | undefined,
    instance_id: string
): ComboboxItem[] => {
    if (!enriched_practitioner_data) return [];

    return (
        enriched_practitioner_data
            ?.find((e) => e.instance_id === instance_id)
            ?.practices?.map((practice) => ({
                value: practice.pvs?.id ?? '',
                label: practice.pvs?.name ?? '',
            })) ?? []
    );
};

export const normalizePrismicHubData = (hub: HubDocument): LocationHub => {
    return {
        id: hub.id,
        key: getTextFromRichTextField(hub.data.key),
        address: getTextFromRichTextField(hub.data.address),
    };
};

/**
 * Check if the practitioner has explicitly disabled appointment confirmation notification
 * @param user_mapping
 * @returns true if explicitly disabled, false if enabled by default or not set
 */
const getApptConfirmationNotificationEnabledFromUser = (
    user_mapping: DCUserMapping | DataCurPracUserMapping
): boolean => {
    if (!user_mapping.notification_settings || user_mapping.notification_settings.length === 0) {
        return true; // enabled by default
    }

    const appt_confirmation_setting = user_mapping.notification_settings?.find(
        (setting) =>
            setting.category === NotificationCategory.APPOINTMENT && setting.type === NotificationType.CONFIRMATION
    );
    if (!appt_confirmation_setting) {
        return true; // enabled by default
    }

    return !appt_confirmation_setting.is_deactivated;
};
